import React, { useMemo, useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

/**
 * SortableTable — a reusable, sortable table for admin lists.
 *
 * Props:
 *  - columns: [{ id, label, accessor(row) | key, sortable?, align?, render?(row, value), width? }]
 *      - accessor: function returning the value for sorting (preferred)
 *      - key: string dotted path shortcut (e.g. 'plan.name')
 *      - sortable: default true
 *      - render: custom cell renderer (otherwise prints the accessor value)
 *  - data: array of rows
 *  - defaultSort: { id, direction: 'asc' | 'desc' }
 *  - onRowClick(row): optional
 *  - rowTestId(row): optional fn returning data-testid for each row
 *  - rowClassName(row): optional fn returning extra className per row
 *  - stickyHeader: boolean (default true)
 *  - emptyMessage: string or node
 */

const resolveKey = (row, key) => {
  if (!key) return undefined;
  return key.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), row);
};

const getValueForSort = (col, row) => {
  if (col.accessor) return col.accessor(row);
  if (col.key) return resolveKey(row, col.key);
  return undefined;
};

const compareValues = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1; // nulls last
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return a === b ? 0 : a ? -1 : 1;
  // Try date parse
  const da = Date.parse(a);
  const db = Date.parse(b);
  if (!isNaN(da) && !isNaN(db)) return da - db;
  // String compare
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

const SortableTable = ({
  columns,
  data,
  defaultSort,
  onRowClick,
  rowTestId,
  rowClassName,
  stickyHeader = true,
  emptyMessage = 'No records found',
  className = '',
  dense = false,
}) => {
  const [sort, setSort] = useState(defaultSort || null);

  const sortedData = useMemo(() => {
    if (!sort?.id) return data;
    const col = columns.find((c) => c.id === sort.id);
    if (!col) return data;
    const dir = sort.direction === 'desc' ? -1 : 1;
    const copy = [...data];
    copy.sort((a, b) => dir * compareValues(getValueForSort(col, a), getValueForSort(col, b)));
    return copy;
  }, [data, sort, columns]);

  const handleHeaderClick = (col) => {
    if (col.sortable === false) return;
    setSort((prev) => {
      if (prev?.id !== col.id) return { id: col.id, direction: 'asc' };
      if (prev.direction === 'asc') return { id: col.id, direction: 'desc' };
      return null; // third click clears sort
    });
  };

  const SortIcon = ({ col }) => {
    if (col.sortable === false) return null;
    if (sort?.id !== col.id) {
      return <ArrowUpDown className="w-3 h-3 opacity-40 group-hover:opacity-70 transition-opacity" />;
    }
    return sort.direction === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-[#002249]" />
    ) : (
      <ArrowDown className="w-3 h-3 text-[#002249]" />
    );
  };

  return (
    <div
      className={`overflow-x-auto bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}
      data-testid="sortable-table"
    >
      <table className="w-full text-left" style={{ minWidth: '720px' }}>
        <thead className={stickyHeader ? 'sticky top-0 z-10' : ''}>
          <tr className="bg-slate-50 border-b border-slate-200">
            {columns.map((col) => (
              <th
                key={col.id}
                onClick={() => handleHeaderClick(col)}
                className={`group select-none ${col.sortable === false ? 'cursor-default' : 'cursor-pointer hover:bg-slate-100'} transition-colors ${dense ? 'px-3 py-2' : 'px-4 py-3'} text-[11.5px] font-semibold uppercase tracking-wider text-slate-600 whitespace-nowrap`}
                style={col.width ? { width: col.width, minWidth: col.width } : undefined}
                data-testid={`col-header-${col.id}`}
                data-sort={sort?.id === col.id ? sort.direction : 'none'}
              >
                <span className={`inline-flex items-center gap-1.5 ${col.align === 'right' ? 'justify-end w-full' : col.align === 'center' ? 'justify-center w-full' : ''}`}>
                  {col.label}
                  <SortIcon col={col} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-16 text-center text-sm text-slate-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedData.map((row, idx) => {
              const isClickable = !!onRowClick;
              return (
                <tr
                  key={row.id || idx}
                  onClick={isClickable ? () => onRowClick(row) : undefined}
                  className={`border-b border-slate-100 transition-[background-color] duration-150 ${
                    isClickable ? 'cursor-pointer hover:bg-slate-50 active:bg-slate-100' : 'hover:bg-slate-50/60'
                  } ${rowClassName ? rowClassName(row) : ''}`}
                  data-testid={rowTestId ? rowTestId(row) : `st-row-${idx}`}
                >
                  {columns.map((col) => {
                    const value = getValueForSort(col, row);
                    const content = col.render ? col.render(row, value) : value == null || value === '' ? <span className="text-slate-300">—</span> : value;
                    return (
                      <td
                        key={col.id}
                        className={`${dense ? 'px-3 py-2' : 'px-4 py-3'} text-[13px] text-slate-700 ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SortableTable;
